import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { journalEntries } from '../../data/journalData';

const JournalFichuaPazia = () => {
  const entry = journalEntries.find(e => e.id === 'fichua-pazia-research');
  const [selectedImage, setSelectedImage] = useState(null);
  const galleryRef = useRef(null);

  // Function to scroll to gallery
  const scrollToGallery = () => {
    if (galleryRef.current) {
      galleryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Gallery images with IDs for navigation
  const galleryImages = [
    { id: 1, url: '/images/journal/FichuaPazia1.jpg', caption: '', description: '' },
    { id: 2, url: '/images/journal/FichuaPazia2.jpg', caption: '', description: '' },
    { id: 3, url: '/images/journal/FichuaPazia3.jpg', caption: '', description: '' },
    { id: 4, url: '/images/journal/FichuaPazia4.jpg', caption: '', description: '' },
    { id: 5, url: '/images/journal/FichuaPazia5.jpg', caption: '', description: '' },
    { id: 6, url: '/images/journal/FichuaPazia6.jpg', caption: '', description: '' },
    { id: 7, url: '/images/journal/FichuaPazia7.jpg', caption: '', description: '' },
    { id: 8, url: '/images/journal/FichuaPazia8.jpg', caption: '', description: '' },
    { id: 9, url: '/images/journal/FichuaPazia9.jpg', caption: '', description: '' },
    { id: 10, url: '/images/journal/FichuaPazia10.jpg', caption: '', description: '' },
    { id: 11, url: '/images/journal/FichuaPazia11.jpg', caption: '', description: '' }
  ];

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;

      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape') {
        closeLightbox();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImage]);

  // Touch/swipe navigation
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  const openLightbox = (image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const goToPrevious = () => {
    const currentIndex = galleryImages.findIndex(img => img.id === selectedImage.id);
    const previousIndex = currentIndex > 0 ? currentIndex - 1 : galleryImages.length - 1;
    setSelectedImage(galleryImages[previousIndex]);
  };

  const goToNext = () => {
    const currentIndex = galleryImages.findIndex(img => img.id === selectedImage.id);
    const nextIndex = currentIndex < galleryImages.length - 1 ? currentIndex + 1 : 0;
    setSelectedImage(galleryImages[nextIndex]);
  };

  if (!entry) {
    return (
      <section className="not-found">
        <div className="container">
          <h2>Entry Not Found</h2>
          <Link to="/research-journal" className="btn">Back to Journal</Link>
        </div>
      </section>
    );
  }

  // Format the content sections
  const objectives = [
    "Raise awareness and understanding of sexual harassment, consent, and exploitation in creative workspaces.",
    "Provide practical tools and policies to reduce harassment and promote accountability.",
    "Strengthen the role of women in leadership and decision-making within creative organizations.",
    "Build sustainable mechanisms for reporting harassment and enforcing safe workplace standards.",
    "Engage industry stakeholders, government bodies, and creative practitioners in co‑creating safer workspaces."
  ];

  const findings = [
    "Women creatives face disproportionate risks in unsafe workplaces due to power imbalances and limited leadership representation.",
    "Men are also affected by harassment, but stigma and social pressure reduce reporting.",
    "The absence of clear workplace policies increases vulnerability across the sector.",
    "Creative workspaces require specialized support structures due to their informal and project‑based nature."
  ];

  const futurePlans = [
    "A new and expanded workplace safety toolkit.",
    "Public art installations that provoke dialogue on safety, power, and accountability in creative spaces.",
    "Institutional training/workshops for universities and creative entrepreneurship programs.",
    "Policy engagement and industry partnerships to strengthen protections for creative workers."
  ];

  const supportNeeds = [
    "Wider distribution of toolkits.",
    "Expanded training programs across creative institutions.",
    "Development of art installations and public engagement platforms.",
    "Stronger research and policy advocacy for safer creative workplaces."
  ];

  return (
    <>
      <section className="journal-entry-header">
        <div className="container">
          <div className="entry-meta-top">
            <span className="research-tag">{entry.researchArea}</span>
          </div>
          <h1>Fichua Pazia – Safer Workspaces in the Kenyan Creative Sector</h1>
          <p className="entry-abstract">{entry.abstract}</p>
        </div>
      </section>

      <section className="journal-entry-content wide-container">
        <div className="container">
          {/* Overview */}
          <div className="entry-section highlight-box">
            <h2>Overview</h2>
            <p>
              Fichua Pazia is a safeguarding initiative under the ZIRAYA Institute working to advance safer and more 
              equitable work environments within Kenya’s creative sector. The project addresses systemic challenges 
              including sexual harassment, exploitation, and power imbalances across creative spaces—from film and 
              theatre sets to production companies and music studios.
            </p>
            <p>
              Grounded in research, advocacy, and practical interventions, Fichua Pazia contributes to shaping 
              industry standards that prioritize safety, dignity, and accountability. The initiative supports 
              creatives; across gender and disciplines, to work in environments that uphold their rights, agency, 
              and well-being.
            </p>
          </div>

          {/* Objectives */}
          <div className="entry-section">
            <h2>Objectives</h2>
            <ul className="project-list">
              {objectives.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>

          {/* Key Activities and Achievements */}
          <div className="entry-section">
            <h2>Key Activities and Achievements</h2>

            <div className="activity-item">
              <h3>Research and Desk Survey</h3>
              <p>A foundational survey examined knowledge, attitudes, and practices around safer workplaces in the creative sector. Findings highlighted common forms of harassment, power dynamics within creative organizations, and gaps in workplace policy and accountability.</p>
            </div>

            <div className="activity-item">
              <h3>Toolkit Development</h3>
              <p>A practical toolkit was created for organizations and employees, providing guidance on anti‑sexual harassment policies, prevention and intervention strategies, reporting mechanisms, and ways to promote equitable workplace practices.</p>
            </div>

            <div className="activity-item">
              <h3>Workshops and Trainings</h3>
              <p>Workshops engaged creative practitioners, managers, and industry stakeholders in discussions on consent, workplace conduct, bystander intervention, leadership inclusion, and accountability mechanisms.</p>
            </div>

            <div className="activity-item">
              <h3>Content and Media Outputs</h3>
              <p>The initiative produced digital content including visual storytelling, documentation, and discussions that sparked industry‑wide dialogue around safety and consent within creative spaces.</p>
            </div>
          </div>
          {/* GALLERY NAVIGATION BUTTON */}
          <div className="gallery-navigation">
            <button onClick={scrollToGallery} className="gallery-nav-btn">
              <i className="fas fa-images"></i>
              <span>Explore the Gallery <i className="fas fa-arrow-down"></i></span>
            </button>
          </div>

          {/* Findings and Insights */}
          <div className="entry-section highlight-box">
            <h2>Findings and Insights</h2>
            <ul className="project-list">
              {findings.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>

          {/* Future Plans */}
          <div className="entry-section">
            <h2>Future Plans</h2>
            <p>The next phase of Fichua Pazia will expand the initiative through:</p>
            <ul className="project-list">
              {futurePlans.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>

          {/* Call for Support */}
          <div className="entry-section cta-box">
            <h2>Call for Support</h2>
            <p>Ziraya Institute is seeking partners and funders to scale Fichua Pazia nationally.</p>
            <p>Support will enable:</p>
            <ul className="project-list">
              {supportNeeds.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className="highlight-text">Investing in safer workspaces strengthens the sustainability and wellbeing of Kenya's creative ecosystem.</p>
          </div>

          {/* Project Leadership */}
          <div className="entry-section creative-lead">
            <h2>Project Leadership</h2>
            <p className="lead-name">Valentine Zikki</p>
            <p>The initiative is led by Valentine Zikki, an artivist and performing arts facilitator with extensive experience in Kenya's music, film, and creative industries. Her work focuses on intellectual property and safeguarding advocacy, creative empowerment, and fostering artistic creation and expression.</p>
            <Link to="/valentine-zikki" className="text-link">Read more about Valentine →</Link>
          </div>

          {/* Key Questions from Journal Data */}
          {entry.keyQuestions && entry.keyQuestions.length > 0 && (
            <div className="key-questions">
              <h2>Key Research Questions</h2>
              <ul>
                {entry.keyQuestions.map((question, index) => (
                  <li key={index}>{question}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Related Project Link */}
          <div className="related-project">
            <h3>Related Project</h3>
            <p>Explore the practical implementation of this research: <Link to="/project/fichua-pazia-unmask" className="btn btn-small">View Fichua Pazia Project →</Link></p>
          </div>

          {/* Resources */}
          {entry.resources && entry.resources.length > 0 && (
            <div className="entry-resources">
              <h2>Resources</h2>
              <div className="resources-list">
                {entry.resources.map((resource, index) => (
                  <div key={index} className="resource-item">
                    <a href={resource.path} className="btn btn-secondary" download>
                      Download {resource.label}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Gallery - with ref for scrolling */}
          <div className="journal-gallery" ref={galleryRef}>
            <h2>Fichua Pazia Research Outcomes</h2>
            <p className="gallery-subtitle"></p>
            <div className="gallery-grid">
              {galleryImages.map((image) => (
                <div
                  key={image.id}
                  className="gallery-item"
                  onClick={() => openLightbox(image)}
                >
                  <div className="gallery-image-wrapper">
                    <img
                      src={image.url}
                      alt={`Fichua Pazia workshop ${image.id}`}
                      className="gallery-image"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/images/placeholder.jpg';
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Collaboration CTA */}
          <div className="entry-collaboration">
            <p className="collaboration-note">
              Ziraya Institute is seeking partners and funders to scale Fichua Pazia nationally.
              If you're interested in supporting this initiative through funding, partnerships, or collaboration,
              please reach out: <a href="mailto:projects@zirayainstitute.org">projects@zirayainstitute.org</a>
            </p>
          </div>

          <div className="back-to-journal">
            <Link to="/research-journal" className="text-link">← Back to Research Journal</Link>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="lightbox-modal"
          onClick={closeLightbox}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <i className="fas fa-times"></i>
            </button>

            <button className="lightbox-nav prev" onClick={goToPrevious}>
              <i className="fas fa-chevron-left"></i>
            </button>

            <button className="lightbox-nav next" onClick={goToNext}>
              <i className="fas fa-chevron-right"></i>
            </button>

            <div className="lightbox-image-wrapper">
              <img
                src={selectedImage.url}
                alt={`Fichua Pazia workshop ${selectedImage.id}`}
                className="lightbox-image"
              />
            </div>

            <div className="lightbox-counter">
              {galleryImages.findIndex(img => img.id === selectedImage.id) + 1} / {galleryImages.length}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default JournalFichuaPazia;